import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Spin, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getAllEmployeeActivity } from "../../Action/Actions";
import { ExportOutlined } from "@ant-design/icons";
import DataTable from "../DataTable/DataTable";
import * as XLSX from "xlsx";
import { getUserData } from "../login/LoginSlice";
import { selectFinancialYears, selectSelectedYearId } from "../../Redux/financialYearSlice";

const RESTRICTED_POLICY_TYPE_IDS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

const EmployeeActivity = () => {
  const dispatch = useDispatch();
  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = useSelector(getUserData);
  const AuthData = useSelector(getUserData);

  const selectedYearId = useSelector(selectSelectedYearId);
  const yearList = useSelector(selectFinancialYears);
  const selectedYear = yearList.find(year => year.id === selectedYearId)?.financial_year || "";

  // This state now drives the API calls and the DataTable's display
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  // UPDATED: This useEffect now handles server-side pagination
  useEffect(() => {
    const fetchEmployeeActivityData = async () => {
      // Guard clause to prevent API calls without necessary IDs
      if (!AuthData?.corporate_id || !selectedYearId) {
        setActivityData([]);
        setPagination(prev => ({ ...prev, total: 0, current: 1 }));
        return;
      }
      try {
        setLoading(true);
        // Pass the current page from state to the thunk
        const data = await dispatch(getAllEmployeeActivity(AuthData.corporate_id, pagination.current));

        // NEW: Filter out restricted policy types
        const filteredData = (data?.data || []).filter(item => {
          const isRestrictedId = RESTRICTED_POLICY_TYPE_IDS.includes(Number(item.policy_type_id));
          const typeName = String(item.policy_type_name || "").toLowerCase();
          const isRestrictedName = typeName.includes('marine') || typeName.includes('fire');
          return !isRestrictedId && !isRestrictedName;
        });

        // FIX: Correctly extract the array and pagination details from the API response
        setActivityData(filteredData);
        setPagination((prev) => ({
          ...prev,
          current: data?.current_page || 1,
          total: data?.total || 0,
          pageSize: data?.per_page || 10,
        }));

      } catch (err) {
        console.error("Error fetching employee activity data:", err);
        notification.error({ message: err.message || "Failed to fetch activity data." });
        setActivityData([]); // Set to empty array on error to prevent crash
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeeActivityData();
    // The dependency array now includes pagination.current to re-fetch on page change
  }, [dispatch, AuthData?.corporate_id, selectedYearId, pagination.current]); 

  // This function is triggered by the DataTable's onChange prop
  const handleTableChange = (newPagination) => {
    // It updates the state, which triggers the useEffect to fetch new data
    setPagination(prev => ({
        ...prev,
        current: newPagination.current,
        pageSize: newPagination.pageSize,
    }));
  };

  const columns = [
    {
      title: "SI NO",
      key: "si_no",
      render: (_, __, index) => {
        // Calculation is now based on server-side pagination
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },
    { title: "POLICY NUMBER", dataIndex: "policy_number", key: "policy_number" },
    { title: "EMPLOYEE NAME", dataIndex: "employee_name", key: "employee_name" },
    { title: "EMPLOYEE CODE", dataIndex: "employee_code", key: "employee_code" },
    { title: "MOBILE NUMBER", dataIndex: "mobile_number", key: "mobile_number" },
    { title: "EMAIL ID", dataIndex: "email", key: "email" },
    { title: "ACTION", dataIndex: "action", key: "action" },
    { title: "DEVICE TYPE", dataIndex: "device_type", key: "device_type" },
    { title: "CREATED AT", dataIndex: "created_at", key: "created_at" },
  ];

  const handleExport = () => {
    if (activityData.length === 0) {
        notification.warning({ message: "No data to export." });
        return;
    }
    // Note: This exports only the data on the current page.
    // For a full export, a different API endpoint without pagination would be needed.
    const ws = XLSX.utils.json_to_sheet(activityData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employee Activity");
    XLSX.writeFile(wb, "Employee_Activity.xlsx");
  };

  return (
    <Spin spinning={loading}>
      <div className="escalation-matrix-container">
        <div className="escalation-heading">Employee Activity</div>
        <div className="escalation-content">
          <Row className="escalation-row-header">
            <span style={{ fontWeight: "700", fontSize: "20px" }}>
              {user?.corporate_name || "Guest"}
            </span>{" "}
            <span style={{ marginLeft: "8px" }}>
              (Financial Year: {selectedYear})
            </span>
          </Row>
          {activityData.length > 0 || loading ? (
          <Card>
            <Row
              gutter={16}
              className="mb-3 justify-content-end align-items-center px-3"
              style={{ marginTop: "16px" }}
            >
              <Col>
                <Button
                  type="primary"
                  icon={<ExportOutlined />}
                  className="d-flex"
                  onClick={handleExport}
                >
                  Export
                </Button>
              </Col>
            </Row>

            <DataTable
              columns={columns}
              dataSource={activityData}
              rowKey={(record, index) => `${record.created_at}-${index}`} // Create a more unique key
              // The pagination prop is now fully controlled by the component's state
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
              }}
              // The onChange handler triggers the state update for new API calls
              onChange={handleTableChange}
            />
          </Card>
          ) : (
            <Card>
              <p>No policy data available for the selected financial year.</p>
            </Card>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default EmployeeActivity;